import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = String(d).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

/**
 * Phase 1003 — Credit Allocations viewer & editor.
 *
 * Opens from the Opening Balance card. Shows every current OB credit
 * allocation for the apartment with invoice number / period /
 * description / invoice amount / allocated amount, plus a summary
 * (credit total, total allocated, remaining). Users can:
 *   • remove an allocation (× button on the row),
 *   • change the allocated amount (inline),
 *   • add a new allocation against any unpaid invoice.
 *
 * On Save, PUT /apartments/{id}/credit-allocations atomically replaces
 * the entire allocation set. The backend caps at:
 *   • sum ≤ |opening_balance|
 *   • each row ≤ invoice remaining (excluding credit-alloc payments)
 * so over-allocation is impossible.
 */
export default function CreditAllocationsDialog({
  open,
  onOpenChange,
  apartmentId,
  onSaved,
  t,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [pickedInvoice, setPickedInvoice] = useState('');
  const [pickedAmount, setPickedAmount] = useState('');

  const fetchData = useCallback(async () => {
    if (!apartmentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/apartments/${apartmentId}/credit-allocations`);
      setSummary(res.data);
      setRows((res.data.allocations || []).map((r, idx) => ({
        row_id: `${r.payment_id}-${r.invoice_id}-${idx}`,
        invoice_id: r.invoice_id,
        invoice_number: r.invoice_number,
        invoice_period: r.invoice_period,
        invoice_date: r.invoice_date,
        invoice_description: r.invoice_description,
        invoice_amount: r.invoice_amount,
        invoice_remaining: r.invoice_remaining,
        allocated: r.allocated_amount,
        orphan: r.orphan,
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load allocations');
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  const fetchUnpaidInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/apartments/${apartmentId}/invoices`);
      const list = (res.data || []).filter(i =>
        !i.is_deleted
        && i.status !== 'credit_note'
        && (i.remaining || 0) > 0.005,
      );
      setUnpaidInvoices(list);
    } catch { setUnpaidInvoices([]); }
  }, [apartmentId]);

  useEffect(() => {
    if (open) {
      fetchData();
      fetchUnpaidInvoices();
    } else {
      setAddOpen(false);
      setPickedInvoice('');
      setPickedAmount('');
    }
  }, [open, fetchData, fetchUnpaidInvoices]);

  const totalAllocated = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.allocated) || 0), 0),
    [rows],
  );
  const creditTotal = summary?.credit_total || 0;
  const remaining = Math.max(creditTotal - totalAllocated, 0);
  const over = Math.max(totalAllocated - creditTotal, 0);

  const removeRow = (row_id) => {
    setRows(prev => prev.filter(r => r.row_id !== row_id));
  };

  const updateAmount = (row_id, value) => {
    setRows(prev => prev.map(r => r.row_id === row_id ? { ...r, allocated: value } : r));
  };

  const addAllocation = () => {
    if (!pickedInvoice) { toast.error(t('selectInvoice') || 'Select an invoice'); return; }
    const amt = parseFloat(pickedAmount) || 0;
    if (amt <= 0) { toast.error(t('enterAmount') || 'Enter an amount > 0'); return; }
    const inv = unpaidInvoices.find(i => i.invoice_id === pickedInvoice);
    if (!inv) return;
    if (rows.some(r => r.invoice_id === inv.invoice_id)) {
      toast.error(t('alreadyAllocated') || 'Invoice already in allocation list');
      return;
    }
    setRows(prev => [...prev, {
      row_id: `new-${inv.invoice_id}-${Date.now()}`,
      invoice_id: inv.invoice_id,
      invoice_number: inv.invoice_number,
      invoice_period: inv.period,
      invoice_date: inv.invoice_date,
      invoice_description: inv.description || inv.notes || (inv.category || 'Common Expenses'),
      invoice_amount: inv.amount,
      invoice_remaining: inv.remaining,
      allocated: amt,
    }]);
    setPickedInvoice('');
    setPickedAmount('');
    setAddOpen(false);
  };

  const save = async () => {
    if (over > 0.005) {
      toast.error(`${t('overAllocation') || 'Total allocation exceeds credit by'} €${over.toFixed(2)}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        allocations: rows
          .map(r => ({ invoice_id: r.invoice_id, amount: parseFloat(r.allocated) || 0 }))
          .filter(a => a.amount > 0.005),
      };
      await axios.put(`${API}/apartments/${apartmentId}/credit-allocations`, payload);
      toast.success(t('savedSuccess') || 'Allocations updated');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred') || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!apartmentId) return null;
  const overForRender = over > 0.005;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl rounded-none max-h-[85vh] overflow-hidden flex flex-col" data-testid="credit-allocations-dialog">
        <DialogHeader>
          <DialogTitle>{t('creditAllocations') || 'Opening Balance Credit Allocations'}</DialogTitle>
        </DialogHeader>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="border border-zinc-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('creditTotal') || 'Credit Total'}</div>
            <div className="text-lg font-mono font-semibold text-zinc-900" data-testid="ca-credit-total">&euro;{creditTotal.toFixed(2)}</div>
          </div>
          <div className={`border px-3 py-2 ${overForRender ? 'border-red-300 bg-red-50/60' : 'border-zinc-200'}`}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('totalAllocated') || 'Total Allocated'}</div>
            <div className={`text-lg font-mono font-semibold ${overForRender ? 'text-red-700' : 'text-zinc-900'}`} data-testid="ca-total-allocated">
              &euro;{totalAllocated.toFixed(2)}
            </div>
          </div>
          <div className="border border-zinc-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('remainingAvailable') || 'Remaining Available'}</div>
            <div className="text-lg font-mono font-semibold text-emerald-700" data-testid="ca-remaining">&euro;{remaining.toFixed(2)}</div>
          </div>
        </div>

        {overForRender && (
          <div className="border border-red-300 bg-red-50/70 px-3 py-2 text-xs text-red-800" data-testid="ca-over-warning">
            {t('overAllocationWarning') || 'Total allocated exceeds available credit by'} &euro;{over.toFixed(2)}. {t('adjustBeforeSaving') || 'Adjust before saving.'}
          </div>
        )}

        {/* Rows */}
        <div className="flex-1 overflow-y-auto border border-zinc-200 mt-2">
          {loading ? (
            <div className="p-6 text-center text-sm text-zinc-500">{t('loading') || 'Loading…'}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">{t('noAllocations') || 'No allocations yet.'}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[11px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2 text-left">{t('invoice') || 'Invoice'}</th>
                  <th className="px-3 py-2 text-left">{t('invoiceDate') || 'Date'}</th>
                  <th className="px-3 py-2 text-left">{t('description') || 'Description'}</th>
                  <th className="px-3 py-2 text-right">{t('invoiceAmount') || 'Original €'}</th>
                  <th className="px-3 py-2 text-right">{t('allocatedAmount') || 'Applied €'}</th>
                  <th className="px-3 py-2 text-right">{t('invoiceRemaining') || 'Remaining €'}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map(r => {
                  const applied = parseFloat(r.allocated) || 0;
                  const invAmt = parseFloat(r.invoice_amount) || 0;
                  // Remaining shown = original invoice amount minus THIS allocation
                  // (already-persisted allocation was included in invoice_remaining
                  // returned by the backend, so we compute a fresh preview).
                  const remainingPreview = Math.max(invAmt - applied, 0);
                  return (
                    <tr key={r.row_id} data-testid={`ca-row-${r.invoice_id}`} className={r.orphan ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 font-mono text-xs">{r.invoice_number}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{formatDateDMY(r.invoice_date) || r.invoice_period || '—'}</td>
                      <td className="px-3 py-2 text-xs text-zinc-700 truncate max-w-[180px]" title={r.invoice_description}>
                        {r.invoice_description}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">&euro;{invAmt.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.allocated}
                          onChange={e => updateAmount(r.row_id, e.target.value)}
                          className="rounded-none h-7 font-mono text-xs text-right w-24 ml-auto"
                          data-testid={`ca-amount-${r.invoice_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-zinc-500" data-testid={`ca-remaining-${r.invoice_id}`}>
                        &euro;{remainingPreview.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(r.row_id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`ca-remove-${r.invoice_id}`}
                          title={t('removeAllocation') || 'Remove allocation'}
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Add allocation */}
        {addOpen ? (
          <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 flex flex-wrap items-end gap-2 mt-2" data-testid="ca-add-row">
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('invoice') || 'Invoice'}</label>
              <select
                value={pickedInvoice}
                onChange={e => setPickedInvoice(e.target.value)}
                className="w-full border border-zinc-300 h-8 px-2 text-xs bg-white"
                data-testid="ca-add-invoice"
              >
                <option value="">— {t('selectInvoice') || 'Select invoice'} —</option>
                {unpaidInvoices
                  .filter(i => !rows.some(r => r.invoice_id === i.invoice_id))
                  .map(i => (
                    <option key={i.invoice_id} value={i.invoice_id}>
                      {i.invoice_number} · {i.period || '—'} · €{(i.remaining || 0).toFixed(2)} {t('remaining') || 'remaining'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="w-24">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('amount') || 'Amount'} (&euro;)</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={pickedAmount}
                onChange={e => setPickedAmount(e.target.value)}
                className="rounded-none h-8 font-mono text-xs"
                data-testid="ca-add-amount"
              />
            </div>
            <Button type="button" onClick={addAllocation} className="bg-zinc-950 rounded-none h-8 text-xs" data-testid="ca-add-confirm">
              {t('add') || 'Add'}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setAddOpen(false); setPickedInvoice(''); setPickedAmount(''); }} className="rounded-none h-8 text-xs">
              {t('cancel') || 'Cancel'}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setAddOpen(true)}
            className="rounded-none h-8 text-xs mt-2 self-start"
            data-testid="ca-add-open"
            disabled={unpaidInvoices.filter(i => !rows.some(r => r.invoice_id === i.invoice_id)).length === 0}
          >
            <Plus size={13} className="mr-1" />
            {t('addAllocation') || 'Add Allocation'}
          </Button>
        )}

        <DialogFooter className="mt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="ca-cancel">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            type="button"
            onClick={save}
            className="bg-zinc-950 rounded-none"
            disabled={saving || overForRender}
            data-testid="ca-save"
          >
            {saving ? (t('saving') || 'Saving…') : (t('saveChanges') || 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
